/**
 * /system-admin/admins — system admin role management (A6.5).
 *
 * Page accessible to both admin and root tiers (system-admin guard on parent
 * layout handles the admin-or-above check). Root sees Grant button + Actions
 * column; admin-tier sees read-only list with a readOnly hint.
 *
 * requireRoot() removed from beforeLoad — the spec requires admin-tier to see
 * the list in read-only mode; only the Grant/Revoke UI is root-gated in-component.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { ShieldCheck } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { SystemAdminGuard } from "@/features/system-admin/components/system-admin-guard";
import { useSystemAdmins } from "@/features/system-admin/api/admins";
import { useIsSystemRoot } from "@/features/system-admin/hooks/use-system-role";
import { AdminsTable } from "@/features/system-admin/components/admins/admins-table";
import { GrantRoleModal } from "@/features/system-admin/components/admins/grant-role-modal";

export const Route = createFileRoute("/system-admin/admins")({
  component: SystemAdminAdminsPage,
});

function SystemAdminAdminsPage() {
  const tc = useTranslations("system-admin.common");
  const t = useTranslations("system-admin.admins");
  const isRoot = useIsSystemRoot();
  const [grantOpen, setGrantOpen] = useState(false);

  const { data, isLoading, isFetching, refetch } = useSystemAdmins();

  const rows = data?.data ?? [];
  const total = data?.paging?.total ?? 0;

  return (
    <SystemAdminGuard>
      <div className="space-y-6">
        <PageHeader
          icon={ShieldCheck}
          title={tc("pages.admins.title")}
          description={tc("pages.admins.subtitle")}
          onReload={() => refetch()}
          isReloading={isFetching}
          totalLabel={total > 0 ? t("meta.found", { count: total }) : undefined}
          actions={
            isRoot ? (
              <Button variant="primary" onClick={() => setGrantOpen(true)}>
                {t("actions.grant")}
              </Button>
            ) : undefined
          }
        />

        {!isRoot && (
          <p className="text-sm text-[var(--muted-foreground)]">{t("meta.readOnly")}</p>
        )}

        <AdminsTable data={rows} isLoading={isLoading} isRoot={isRoot} />

        <GrantRoleModal isOpen={grantOpen} onClose={() => setGrantOpen(false)} />
      </div>
    </SystemAdminGuard>
  );
}
